import type { AuthPayload } from "@/types/user.types";

export const cipherEmail = (email: string | undefined): string => {
  if (!email) return "";
  const [local, domain] = email.split("@");
  if (!domain) return email; // invalid email safeguard

  // Hide part of local (show only first 2 chars)
  const visibleLocal = local.slice(0, 2);
  const hiddenLocal = local.length > 2 ? "**" : "*";

  // Process domain
  const lastDotIndex = domain.lastIndexOf(".");
  if (lastDotIndex === -1) return `${visibleLocal}${hiddenLocal}@**`;

  const tld = domain.slice(lastDotIndex); // .com, .org, etc.
  return `${visibleLocal}${hiddenLocal}@**${tld}`;
};

// Email validation helper
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation helper
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
  return phoneRegex.test(phone);
};


// Strong password validation
export const isStrongPassword = (password: string): boolean => {
  const hasLetters = /[a-zA-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  return hasLetters && hasNumbers && hasSymbols;
};

export const validatePassword = (password: string) => {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  if (!/(?=.*[@$!%*?&])/.test(password)) return 'Password must contain at least one special character';
  return null;
};

// Check password strength
export const checkPasswordStrength = (password: string): "Weak" | "Medium" | "Strong" => {
  if (password.length < 8) {
    return 'Weak';
  } else if (password.length < 12 || !isStrongPassword(password)) {
    return 'Medium';
  } else {
    return 'Strong';
  }
};

// Check if a character is a digit
export const isDigit = (char: string): boolean => {
  return /^\d$/.test(char);
};

export const getDecodedToken = (token: string | undefined): AuthPayload | null => {
  // Decode the JWT refresh token
  const decoded = token
    ? JSON.parse(atob(token?.split('.')[1] || ''))
    : {};
  return decoded;
};

export const utcToLocaleYYYYMMDDandTime = (utcString: string | undefined): string => {
  if (!utcString) return "";
  const date = new Date(utcString);
  return date.toLocaleString(); // Format: "MM/DD/YYYY, HH:MM:SS AM/PM"
};